import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {
  Document,
  DocumentVersion,
  FromServerDocument,
  FromServerDocumentVersion,
  ToServerDocument,
  ToServerDocumentVersion,
} from 'notebook/documents/DocumentConstants';
import {EntryVersion} from 'notebook/ELNConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import {ServerDocumentEditorLock} from 'notebook/realtime/DocumentEditorLockConstants';
import {DocumentEditorLock} from 'notebook/realtime/reduceDocumentEditorLockState';
import {UPDATE_ALL_ITEM_PARTS} from 'notebook/store/auto-save/ItemPartSaveTracker';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
// @ts-expect-error
import EntryVersionUtil from 'notebook/util/EntryVersionUtil';
import {Note, EntryNote, NotePart} from 'notebook/util/NoteUtilConstants';
import pick from 'util/pick';

const DocumentUtil = {
  entryVersionUpdate(op: Spec<EntryVersion>): Spec<Document> {
    return {documentVersion: {entryVersion: op}};
  },

  mergeEntryVersionUpdateWithNote(note: EntryNote): Spec<Document> {
    return {documentVersion: {entryVersion: {$merge: {note}}}};
  },

  sanitizeDocument(document: Document, oldDocument: Document | null = null): Document {
    return reactUpdate(
      document,
      DocumentUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion): EntryVersion {
          return EntryVersionUtil.sanitizeEntryVersion(
            entryVersion,
            oldDocument != null ? oldDocument.documentVersion.entryVersion : undefined,
            document.id
          );
        },
      })
    );
  },

  removeTasksFromDocument(document: Document): Document {
    return reactUpdate(
      document,
      DocumentUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion): EntryVersion {
          const note = NoteStoreTransforms.removeTasksFromNote(entryVersion.note);
          return {...entryVersion, note};
        },
      })
    );
  },

  prepareForStore(document: FromServerDocument, cidIdMap: CidIdMap): Omit<Document, 'latestVersionId'> {
    const {
      id,
      name,
      permissions,
      creator,
      createdAt,
      modifiedAt,
      sectionConfiguration,
      canvasConfiguration,
      isConfig,
      lock,
      tablesToInsertOnCreation,
      customInsertableTables,
    } = document;
    return {
      id,
      name,
      permissions,
      creator,
      createdAt,
      lock: DocumentUtil.prepareLockForStore(lock),
      modifiedAt,
      sectionConfiguration,
      canvasConfiguration,
      isConfig,
      documentVersion: DocumentUtil.prepareDVForStore(document.documentVersion, cidIdMap),
      isStale: false,
      tablesToInsertOnCreation,
      customInsertableTables,
    };
  },

  prepareLockForStore(lock?: ServerDocumentEditorLock): DocumentEditorLock | undefined {
    return lock
      ? {
          clientId: lock.clientId,
          userId: lock.userId,
          expiresAt: new Date(Date.now() + lock.msUntilExpiration),
        }
      : undefined;
  },

  prepareDVForStore(
    documentVersion: FromServerDocumentVersion,
    cidIdMap: CidIdMap
  ): {
    cid: string;
    entryVersion: EntryVersion;
  } {
    const cid = _.uniqueId('dv');
    cidIdMap[cid] = documentVersion.id;
    return {
      cid,
      entryVersion: EntryVersionUtil.prepareForStore(documentVersion.entryVersion, cidIdMap),
    };
  },

  prepareForSave(
    document: Document,
    cidIdMap: CidIdMap,
    unsavedNoteParts: Array<NotePart> | typeof UPDATE_ALL_ITEM_PARTS | undefined
  ): ToServerDocument {
    return {
      name: document.name,
      documentVersion: DocumentUtil.prepareDVForSave(
        document.documentVersion,
        cidIdMap,
        document.id,
        unsavedNoteParts
      ),
    };
  },

  prepareDVForSave(
    documentVersion: DocumentVersion,
    cidIdMap: CidIdMap,
    fileId: string,
    unsavedNoteParts: Array<NotePart> | typeof UPDATE_ALL_ITEM_PARTS | undefined
  ): ToServerDocumentVersion {
    return {
      id: cidIdMap[documentVersion.cid] as number,
      cid: documentVersion.cid,
      entryVersion: EntryVersionUtil.prepareForSave(
        documentVersion.entryVersion,
        cidIdMap,
        fileId,
        unsavedNoteParts
      ),
    };
  },

  pickRevertableFields(document: Document): {name: string; documentVersion: DocumentVersion} {
    return pick(document, 'name', 'documentVersion');
  },

  toEntryNote(note: Note): EntryNote {
    return note.days ? note : {...note, days: []};
  },

  getTableMappings(document: Document): Array<StructuredTableMapping> {
    return document.documentVersion.entryVersion.structuredTableMappings;
  },

  isDocumentEmpty(document: Document): boolean {
    const {noteParts} = document.documentVersion.entryVersion.note;
    if (
      !assertAndContinue(noteParts.length !== 0, 'Document found with no note parts', {
        dvid: document.documentVersion.id,
      })
    ) {
      return true;
    }
    if (noteParts.length > 1) {
      return false;
    }
    const onlyNotePart = noteParts[0];
    return onlyNotePart.type === 'text' && onlyNotePart.text.length === 0;
  },
};

export default DocumentUtil;
